import React, { useState } from 'react';
import { TruckIcon, PlusIcon, EditIcon, AlertTriangleIcon, CheckCircleIcon, WrenchIcon, UserIcon, CalendarIcon } from 'lucide-react';
interface Truck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  plateNumber: string;
  status: 'available' | 'assigned' | 'maintenance' | 'out-of-service';
  lastMaintenance: string;
  nextMaintenance: string;
  mileage: number;
  currentHours: number;
  hoursSinceLastMaintenance: number;
  maintenanceIntervalHours: number;
  assignedDriver?: string;
  currentJob?: string;
}
interface Driver {
  id: string;
  name: string;
}
interface FleetManagementProps {
  trucks: Truck[];
  drivers: Driver[];
}
export const FleetManagement = ({
  trucks,
  drivers
}: FleetManagementProps) => {
  const [showAddTruckModal, setShowAddTruckModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-orange-100 text-orange-800';
      case 'out-of-service':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-3673" />;
      case 'assigned':
        return <UserIcon size={16} className="text-blue-500" data-id="element-3674" />;
      case 'maintenance':
        return <WrenchIcon size={16} className="text-orange-500" data-id="element-3675" />;
      case 'out-of-service':
        return <AlertTriangleIcon size={16} className="text-red-500" data-id="element-3676" />;
      default:
        return <TruckIcon size={16} className="text-gray-500" data-id="element-3677" />;
    }
  };
  const getDriverName = (driverId?: string) => {
    if (!driverId) return 'Unassigned';
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || 'Unknown Driver';
  };
  const isMaintenanceDue = (truck: Truck) => {
    const hoursRemaining = truck.maintenanceIntervalHours - truck.hoursSinceLastMaintenance;
    return hoursRemaining <= 50; // Due soon if within 50 hours
  };
  const isMaintenanceOverdue = (truck: Truck) => {
    return truck.hoursSinceLastMaintenance >= truck.maintenanceIntervalHours;
  };
  return <div className="bg-white rounded-md shadow p-4 lg:p-6" data-id="element-3678">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 lg:mb-6 gap-3" data-id="element-3679">
        <div data-id="element-3680">
          <h2 className="text-lg lg:text-xl font-bold" data-id="element-3681">Fleet Management</h2>
          <p className="text-sm text-gray-600 mt-1" data-id="element-3682">
            Manage your truck fleet, assignments, and maintenance schedules
          </p>
        </div>
        <button onClick={() => setShowAddTruckModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm lg:text-base w-full sm:w-auto justify-center" data-id="element-3683">
          <PlusIcon size={18} data-id="element-3684" />
          Add New Truck
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6" data-id="element-3685">
        {trucks.map(truck => <div key={truck.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-3686">
            <div className="flex items-center justify-between mb-3" data-id="element-3687">
              <div className="flex items-center gap-3" data-id="element-3688">
                <TruckIcon size={20} className="text-blue-500" data-id="element-3689" />
                <div data-id="element-3690">
                  <h3 className="font-semibold text-sm lg:text-base" data-id="element-3691">
                    {truck.truckNumber}
                  </h3>
                  <p className="text-xs lg:text-sm text-gray-500" data-id="element-3692">
                    {truck.make} {truck.model}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelectedTruck(truck)} className="text-gray-400 hover:text-gray-600 p-1" data-id="element-3693">
                <EditIcon size={16} data-id="element-3694" />
              </button>
            </div>
            <div className="space-y-2 mb-4" data-id="element-3695">
              <div className="flex justify-between text-xs lg:text-sm" data-id="element-3696">
                <span className="text-gray-500" data-id="element-3697">Type:</span>
                <span className="font-medium" data-id="element-3698">{truck.type}</span>
              </div>
              <div className="flex justify-between text-xs lg:text-sm" data-id="element-3699">
                <span className="text-gray-500" data-id="element-3700">Year:</span>
                <span data-id="element-3701">{truck.year}</span>
              </div>
              <div className="flex justify-between text-xs lg:text-sm" data-id="element-3702">
                <span className="text-gray-500" data-id="element-3703">Plate:</span>
                <span data-id="element-3704">{truck.plateNumber}</span>
              </div>
              <div className="flex justify-between text-xs lg:text-sm" data-id="element-3705">
                <span className="text-gray-500" data-id="element-3706">Mileage:</span>
                <span data-id="element-3707">{truck.mileage.toLocaleString()} mi</span>
              </div>
              <div className="flex justify-between text-xs lg:text-sm" data-id="element-3708">
                <span className="text-gray-500" data-id="element-3709">Driver:</span>
                <span className="text-right" data-id="element-3710">
                  {getDriverName(truck.assignedDriver)}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between mb-3" data-id="element-3711">
              <div className="flex items-center gap-2" data-id="element-3712">
                {getStatusIcon(truck.status)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(truck.status)}`} data-id="element-3713">
                  {truck.status.replace('-', ' ').toUpperCase()}
                </span>
              </div>
              {truck.currentJob && <span className="text-xs text-blue-600" data-id="element-3714">
                  Job: {truck.currentJob}
                </span>}
            </div>
            <div className="border-t pt-3" data-id="element-3715">
              <div className="flex items-center gap-2 text-xs lg:text-sm mb-2" data-id="element-3716">
                <CalendarIcon size={12} className="text-gray-400" data-id="element-3717" />
                <span className="text-gray-500" data-id="element-3718">Maintenance Status:</span>
              </div>
              <div className="space-y-1" data-id="element-3719">
                <div className="flex justify-between text-xs" data-id="element-3720">
                  <span data-id="element-3721">Current Hours:</span>
                  <span className="font-medium" data-id="element-3722">
                    {truck.currentHours.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-3723">
                  <span data-id="element-3724">Since Last Service:</span>
                  <span className={isMaintenanceOverdue(truck) ? 'text-red-600 font-medium' : isMaintenanceDue(truck) ? 'text-orange-600 font-medium' : 'text-gray-700'} data-id="element-3725">
                    {truck.hoursSinceLastMaintenance} hrs
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-3726">
                  <span data-id="element-3727">Service Interval:</span>
                  <span className="text-gray-700" data-id="element-3728">
                    {truck.maintenanceIntervalHours} hrs
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-3729">
                  <span data-id="element-3730">Hours Until Service:</span>
                  <span className={isMaintenanceOverdue(truck) ? 'text-red-600 font-medium' : isMaintenanceDue(truck) ? 'text-orange-600 font-medium' : 'text-green-600'} data-id="element-3731">
                    {Math.max(0, truck.maintenanceIntervalHours - truck.hoursSinceLastMaintenance)}{' '}
                    hrs
                  </span>
                </div>
              </div>
              {(isMaintenanceDue(truck) || isMaintenanceOverdue(truck)) && <div className={`mt-2 border rounded-md p-2 ${isMaintenanceOverdue(truck) ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`} data-id="element-3732">
                  <p className={`text-xs flex items-center gap-1 ${isMaintenanceOverdue(truck) ? 'text-red-600' : 'text-orange-600'}`} data-id="element-3733">
                    <AlertTriangleIcon size={12} data-id="element-3734" />
                    {isMaintenanceOverdue(truck) ? 'Service overdue' : 'Service due soon'}
                  </p>
                </div>}
            </div>
          </div>)}
      </div>
      {trucks.length === 0 && <div className="text-center py-8 lg:py-12" data-id="element-3735">
          <TruckIcon size={40} className="mx-auto text-gray-400 mb-4 lg:w-12 lg:h-12" data-id="element-3736" />
          <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2" data-id="element-3737">
            No trucks in your fleet
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3738">
            Add your first truck to start managing your fleet
          </p>
          <button onClick={() => setShowAddTruckModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm lg:text-base" data-id="element-3739">
            Add Your First Truck
          </button>
        </div>}
      {/* Add Truck Modal */}
      {showAddTruckModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-3740">
          <div className="bg-white rounded-lg p-4 lg:p-6 w-full max-w-md mx-4" data-id="element-3741">
            <h3 className="text-lg font-semibold mb-4" data-id="element-3742">Add New Truck</h3>
            <p className="text-gray-600 mb-4" data-id="element-3743">
              Add truck form would be implemented here
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3" data-id="element-3744">
              <button onClick={() => setShowAddTruckModal(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm lg:text-base" data-id="element-3745">
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm lg:text-base" data-id="element-3746">
                Add Truck
              </button>
            </div>
          </div>
        </div>}
    </div>;
};